import { HL7 } from '@/consts/systems.consts';
import { ConsentData } from '@/types/consent.types';

export interface ConsentValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface ConsentValidationOptions {
  t: (key: string, options?: any) => string;
  /** Count of existing authorization documents that are already saved and not marked for deletion */
  existingAuthorizationDocumentCount?: number;
}

/**
 * Validates consent data according to business rules
 * @param data - The consent data to validate
 * @param options - Validation options including translation function
 * @returns Validation result with isValid flag and error messages
 */
export function validateConsentData(
  data: ConsentData,
  options: ConsentValidationOptions
): ConsentValidationResult {
  const { t } = options;
  const errors: string[] = [];

  // Validate signer type is selected
  if (!data.signerType) {
    errors.push(
      t('consent.validation.signerTypeRequired', {
        ns: 'lims',
        defaultValue: 'Please select who is signing the consent',
      })
    );
  }

  // Validate related person selection for 'other' signer type
  if (data.signerType === 'other') {
    if (
      !data.selectedRelatedPerson &&
      (!data.newRelatedPersonName || !data.newRelatedPersonRelationship)
    ) {
      errors.push(
        t('consent.validation.relatedPersonRequired', {
          ns: 'lims',
          defaultValue:
            'Please select an existing related person or create a new one',
        })
      );
    }
  }

  // Authorization document is required for authorized persons
  if (
    data.selectedRelatedPerson?.relationship?.[0]?.coding?.[0]?.code ===
      HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.POWER_OF_ATTORNEY ||
    data.newRelatedPersonRelationship === 'power of attorney'
  ) {
    // Check if there's an existing authorization document from a previous save
    const hasExistingAuthDoc =
      (options.existingAuthorizationDocumentCount ?? 0) > 0;
    // Check if there's a new file being uploaded
    const hasNewAuthDoc =
      data.authorizationDocument !== undefined &&
      data.authorizationDocument !== null;

    if (!hasExistingAuthDoc && !hasNewAuthDoc) {
      errors.push(
        t('consent.validation.missingConsent.authorizationDocumentRequired', {
          ns: 'lims',
          defaultValue:
            'Authorization document is required for authorized persons',
        })
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
